import ScButton from "@/src/components/ui-kit/button";
import DropDown from "@/src/components/ui-kit/drop-down";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import { usePermissions } from "@/src/hooks/use-permissions";
import { useTheme } from "@/src/styles/theme-provider";
import { EventOrganizerSnapshot } from "@/src/models/event.model";
import { Global, MinusCirlce, UserAdd } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Image, Pressable, Text, View } from "react-native";
import { SectionCard } from "./event-detail-section-card";

type AvatarItem = Readonly<{
  userId: string;
  profileImageUrl: string;
}>;

type EventCoverProps = Readonly<{
  uri?: string;
  title: string;
  visibilityLabel?: string;
  categories: readonly string[];
  author?: EventOrganizerSnapshot;
  authorId?: string;
  attendingCount: number;
  visibleAvatars: readonly AvatarItem[];
  isAuthor: boolean;
  isOngoing?: boolean;
  isArchived?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onJoin?: () => void;
  onArchive?: () => void;
  showJoin?: boolean;
  joinButtonText?: string;
  isJoiningOrLeaving?: boolean;
  onParticipantsPress?: () => void;
  isAttending?: boolean;
  isWaitlisted?: boolean;
}>;

const CATEGORY_COLORS: readonly Readonly<{ bg: string; text: string }>[] = [
  { bg: "bg-orange-100", text: "text-orange-600" },
  { bg: "bg-blue-100", text: "text-blue-600" },
  { bg: "bg-green-100", text: "text-green-600" },
];

type CategoryChipProps = Readonly<{
  label: string;
  colorIndex: number;
}>;

const CategoryChip = ({ label, colorIndex }: CategoryChipProps) => {
  const color = CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length];
  return (
    <View className={`px-2.5 py-1 rounded-full ${color.bg}`}>
      <Text className={`text-xs font-medium ${color.text}`}>{label}</Text>
    </View>
  );
};

const EventCover = ({
  uri,
  title,
  visibilityLabel,
  categories = [],
  author,
  authorId,
  attendingCount,
  visibleAvatars,
  isAuthor,
  isOngoing = false,
  isArchived = false,
  onEdit,
  onArchive,
  onDelete,
  onJoin,
  joinButtonText,
  isJoiningOrLeaving,
  showJoin,
  onParticipantsPress,
  isAttending,
  isWaitlisted,
}: EventCoverProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { canUpdate, canDelete } = usePermissions();

  const attendingLabel = `${attendingCount} ${attendingCount === 1 ? t("LABEL_PERSON_JOINED") : t("LABEL_PEOPLE_JOINED")}`;
  const archiveMenuItem = isArchived
    ? { label: t("LABEL_UNARCHIVE"), onPress: onArchive }
    : { label: t("LABEL_ARCHIVE"), onPress: onArchive };

  const menuItems = [
    ...(isAuthor && canUpdate("event")
      ? [{ label: t("LABEL_EDIT"), onPress: () => onEdit?.() }]
      : []),
    { label: archiveMenuItem.label, onPress: () => onArchive?.() },
    ...(isAuthor && !isOngoing && canDelete("event")
      ? [
          {
            label: t("LABEL_DELETE"),
            onPress: () => onDelete?.(),
            destructive: true,
          },
        ]
      : []),
  ];

  return (
    <View>
      <View className="h-52 md:h-64 -mx-4 overflow-hidden bg-chalk">
        {uri ? (
          <Image
            source={{ uri }}
            className="w-full h-full"
            resizeMode="cover"
          />
        ) : (
          <View className="w-full h-full bg-ash-3" />
        )}
      </View>

      <View className="mx-2 md:mx-5" style={{ marginTop: -40 }}>
        <SectionCard>
          <View className="flex-row items-start justify-between">
            <Text className="text-xl font-semibold flex-1 pr-2 text-onyx">
              {title}
            </Text>
            <DropDown iconColor={"gray"} iconSize={18} items={menuItems} />
          </View>

          <View className="flex-row flex-wrap items-center gap-3 mt-2">
            {visibilityLabel && (
              <View className="flex-row items-center gap-1">
                <Global
                  size={20}
                  color={themeVars["--color-primary-default"] as string}
                />
                <Text className="text-xs font-semibold capitalize text-primary">
                  {visibilityLabel}
                </Text>
              </View>
            )}
            {categories.map((category, index) => (
              <CategoryChip
                key={category}
                label={category}
                colorIndex={index}
              />
            ))}
          </View>

          <View className="flex-row items-center justify-between gap-2 flex-wrap mt-2">
            {/* Author */}
            <View className="flex-row items-center gap-2 min-w-0 shrink">
              <BlocksAvatar
                userId={authorId}
                fallbackImageUrl={author?.profileImageUrl}
                size={32}
                className="rounded-full border border-ash-3 shrink-0"
              />
              <View className="min-w-0">
                <Text
                  className="text-xs font-semibold text-onyx"
                  numberOfLines={1}
                >
                  {author?.firstName} {author?.lastName}
                </Text>
              </View>

              {/* Avatars — pressable to open participants */}
              {visibleAvatars.length > 0 && (
                <Pressable
                  onPress={onParticipantsPress}
                  className="flex-row items-center gap-2 ml-2 px-2 py-1 rounded-full bg-ash-2 border border-ash-3 hover:bg-ash-3"
                >
                  <View className="flex-row">
                    {visibleAvatars.map((participant, index) => (
                      <BlocksAvatar
                        key={participant.userId}
                        userId={participant.userId}
                        fallbackImageUrl={participant.profileImageUrl}
                        size={24}
                        className={`border-2 border-ash-3 ${index !== 0 ? "-ml-2" : ""}`}
                      />
                    ))}
                  </View>
                  <Text className="text-xs font-medium text-onyx">
                    {attendingLabel}
                  </Text>
                </Pressable>
              )}
            </View>

            {showJoin && (
              <ScButton
                text={joinButtonText ?? "BTN_JOIN"}
                mode={isAttending || isWaitlisted ? "outlined" : "contained"}
                onPress={onJoin}
                loading={isJoiningOrLeaving}
                icon={
                  isAttending || isWaitlisted ? (
                    <MinusCirlce size={16} className="text-primary" />
                  ) : (
                    <UserAdd size={16} className="text-white" />
                  )
                }
              />
            )}
          </View>
        </SectionCard>
      </View>
    </View>
  );
};

export default EventCover;
